/**
 * Wrapper around generated RTK Query API with cleaner naming and custom logic.
 *
 * All endpoint references (keys, hooks, endpoint map) are derived from the
 * central definitions in `./endpoints.ts` so generated names live in one place.
 */
import { generatedApi } from './api.generated';
import { ITEMS_API_ENDPOINTS as cfg } from './config';
import {
  createEndpointExports,
  createHookExports,
  provideListTags,
  LIST_TAG_ID,
} from '@skynet/data-helpers';
import type { HookExports } from '@skynet/data-helpers';

/**
 * Cache tags follow the recommended `id: 'LIST'` recipe:
 * https://redux-toolkit.js.org/rtk-query/usage/automated-refetching#recipes
 *
 * A single `Items` tag type is used for both the collection and individual
 * entities. The list query provides a per-item tag for every item plus a
 * dedicated `{ type: 'Items', id: 'LIST' }` tag, so that:
 * - updating/deleting/(un)publishing a single item invalidates only that
 *   item's `{ id }` tag, which refetches both the affected `getItem` query and
 *   the list (the list provides that same per-item tag);
 * - creating an item invalidates `{ id: 'LIST' }` to refetch the collection
 *   without touching unrelated entities.
 *
 * This replaces the previous manual `dispatch(invalidateTags(...))` workaround in
 * `onQueryStarted`: that was attributed to redux-toolkit#5093, but that issue is
 * about the `isSuccess` flag during pessimistic updates, not tag invalidation —
 * `invalidatesTags` propagates to the list correctly via the per-item tags.
 */
export const itemsApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['Items'],
  endpoints: {
    [cfg.createItem.generated]: {
      invalidatesTags: [{ type: 'Items', id: LIST_TAG_ID }],
    },
    [cfg.listItems.generated]: {
      providesTags: (result) => provideListTags(result, 'Items'),
    },
    [cfg.getItem.generated]: {
      providesTags: (_result, _error, arg) => [{ type: 'Items', id: arg.id }],
    },
    [cfg.updateItem.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Items', id: arg.id }],
    },
    [cfg.deleteItem.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Items', id: arg.id }],
    },
    [cfg.publishItem.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Items', id: arg.id }],
    },
    [cfg.unpublishItem.generated]: {
      invalidatesTags: (_result, _error, arg) => [{ type: 'Items', id: arg.id }],
    },
  },
});

// Re-export hooks with cleaner names via endpoint objects
export const hookExports: HookExports<typeof cfg, typeof itemsApi> = createHookExports(
  cfg,
  itemsApi,
);

// Re-export endpoints object with cleaner names for direct usage
export const itemsApiEndpoints = createEndpointExports(cfg, itemsApi);
